import Icon from "@avinet/adaptive-ui-core/ui/Icon";
import { t } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { DEFAULT_API_CONFIG } from "../../api/api-config";
import { BioIcon } from "../../assets/icons/BioIcon";
import { CopyClipboardIcon } from "../../assets/icons/CopyClipboardIcon";
import { PaperIcon } from "../../assets/icons/PaperIcon";
import { SortingGradeIcon } from "../../assets/icons/SortingGradeIcon";
import { WasteIcon } from "../../assets/icons/WasteIcon";
import { SortingGrade } from "../../components/diagrams/SortingGrade";
import { WasteAmount } from "../../components/diagrams/WasteAmount";
import { OrderTable } from "../../components/order-table/OrderTable";
import { OrderlineView } from "../../components/orderline-view/OrderlineView";
import { PageLayout } from "../../components/pagelayout/PageLayout";
import { SubscriptionTable } from "../../components/subscription-table/SubscriptionTable";
import { Text } from "../../components/text/Text";
import LOCAL_STORAGE from "../../constants/LocalStorage";
import useDataFetch from "../../hooks/useDataFetch";
import { ICustomer, IPickupDay, ISortingGrade } from "../../utils/data-types";
import { getFromStore } from "../../utils/local-storage";
import "./CustomerInfo.scss";

export const CustomerInfo = () => {
  const [isWaste, setIsWaste] = useState<boolean>();
  const [wastePickupDays, setWastePickupDays] = useState<string[]>([]);
  const [isPaper, setIsPaper] = useState<boolean>();
  const [paperPickupDays, setPaperPickupDays] = useState<string[]>([]);
  const [isBio, setIsBio] = useState<boolean>();
  const [bioPickupDays, setBioPickupDays] = useState<string[]>([]);
  const currentYear = new Date().getFullYear();
  const [compareViewActive, setCompareViewActive] = useState<boolean>(false);
  const [year1, setYear1] = useState<number>(currentYear);
  const [year2, setYear2] = useState<number | undefined>(currentYear);
  const [isMonth, setIsMonth] = useState<boolean>(true);
  const currentMonth = new Date().getMonth() + 1;
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth);
  const [orderId, setOrderId] = useState<number>();
  const [isOrderlineView, setIsOrderlineView] = useState<boolean>(false);
  const [customerId, setCustomerId] = useState<number>();

  const handleSetIsOrderlineViewTrue = useCallback(() => {
    setIsOrderlineView(true);
  }, []);

  const handleSetIsOrderlineViewFalse = useCallback(() => {
    setIsOrderlineView(false);
  }, []);

  const handleSetOrderId = useCallback((orderId: number) => {
    setOrderId(orderId);
  }, []);

  const handleFirstSelect = useCallback(
    (year: number) => {
      setYear1(year);
      if (year2 === undefined || year === year2 || year > year2) {
        setYear2(year + 1);
      }
    },
    [year2]
  );

  const handleSecondSelect = useCallback(
    (year: number | undefined) => {
      setYear2(year);
      if (year && (year === year1 || year < year1)) {
        setYear1(year - 1);
      }
    },
    [year1]
  );

  const [addressCopied, setAddressCopied] = useState(false);

  useEffect(() => {
    const fetchCustomerId = async () => {
      try {
        const storedId = await getFromStore<number>(LOCAL_STORAGE.CUSTOMER);
        if (storedId) setCustomerId(storedId);
      } catch (error) {
        console.error("Error fetching customer info from storage:", error);
      }
    };

    fetchCustomerId();
  }, []);

  function getCustomerUrl(customerId: number | null): string {
    return customerId
      ? DEFAULT_API_CONFIG.url + "/crm/kunde/get/" + customerId
      : "";
  }

  function getPickupDaysUrl(customerId: number | null): string {
    return customerId
      ? DEFAULT_API_CONFIG.url + "/crm/tomming/gettommedag/" + customerId
      : "";
  }

  const sortingGradeUrl = DEFAULT_API_CONFIG.url + "/crm/tomming/getsum";

  const customerUrl = getCustomerUrl(customerId ?? null);

  const [customerData] = useDataFetch<ICustomer[]>(customerUrl, "GET");

  const [pickupDays] = useDataFetch<IPickupDay[]>(
    getPickupDaysUrl(customerId ?? null),
    "GET"
  );

  useEffect(() => {
    const weekdays = [
      "Måndag",
      "Tysdag",
      "Onsdag",
      "Torsdag",
      "Fredag",
      "Laurdag",
      "Søndag",
    ];

    const wasteDays: string[] = [];
    const paperDays: string[] = [];
    const bioDays: string[] = [];

    if (pickupDays) {
      pickupDays.forEach((entry) => {
        if (!weekdays.includes(entry.hentedag)) return;

        switch (entry.fraksjon) {
          case "Rest":
            setIsWaste(true);
            wasteDays.push(entry.hentedag);
            break;
          case "PPP":
            setIsPaper(true);
            paperDays.push(entry.hentedag);
            break;
          case "Bio":
            setIsBio(true);
            bioDays.push(entry.hentedag);
            break;
          default:
            break;
        }
      });
    }

    // Sort the days in the correct order
    wasteDays.sort((a, b) => weekdays.indexOf(a) - weekdays.indexOf(b));
    paperDays.sort((a, b) => weekdays.indexOf(a) - weekdays.indexOf(b));
    bioDays.sort((a, b) => weekdays.indexOf(a) - weekdays.indexOf(b));

    setWastePickupDays(wasteDays);
    setPaperPickupDays(paperDays);
    setBioPickupDays(bioDays);
  }, [pickupDays]);

  const sortingGradeRequest1 = useMemo(() => {
    return {
      kundenummer: customerId,
      year: year1,
      ...(isMonth && { month: selectedMonth }),
    };
  }, [customerId, isMonth, selectedMonth, year1]);

  const sortingGradeRequest2 = useMemo(() => {
    return {
      kundenummer: customerId,
      year: year2,
      ...(isMonth && { month: selectedMonth }),
    };
  }, [customerId, isMonth, selectedMonth, year2]);

  const sortingGradeLastMonthRequest = useMemo(() => {
    return {
      kundenummer: customerId,
      year: currentMonth === 1 ? currentYear - 1 : currentYear,
      month: currentMonth === 1 ? 12 : currentMonth - 1,
    };
  }, [currentMonth, currentYear, customerId]);

  const [dataYear1] = useDataFetch<ISortingGrade[]>(
    sortingGradeUrl,
    "POST",
    sortingGradeRequest1
  );

  const [dataYear2] = useDataFetch<ISortingGrade[]>(
    sortingGradeUrl,
    "POST",
    sortingGradeRequest2
  );

  const [dataLastMonth] = useDataFetch<ISortingGrade[]>(
    sortingGradeUrl,
    "POST",
    sortingGradeLastMonthRequest
  );

  const sortingGradeLastMonthData = useMemo(() => {
    let combustion = 0;
    let recycling = 0;
    for (const entry of dataLastMonth ?? []) {
      if (entry.artikkel === 205) {
        combustion += entry.mengde;
      } else {
        recycling += entry.mengde;
      }
    }

    const total = combustion + recycling;
    const percentage = (recycling / total) * 100;
    return Math.round(percentage * 10) / 10;
  }, [dataLastMonth]);

  const sortingGradeCurrentYearRequest = useMemo(() => {
    return {
      kundenummer: customerId,
      year: currentYear,
    };
  }, [customerId, currentYear]);

  const sortingGradeLastYearRequest = useMemo(() => {
    return {
      kundenummer: customerId,
      year: currentYear - 1,
    };
  }, [customerId, currentYear]);

  const [sortingGradeCurrentYear] = useDataFetch<ISortingGrade[]>(
    sortingGradeUrl,
    "POST",
    sortingGradeCurrentYearRequest
  );

  const [sortingGradeLastYear] = useDataFetch<ISortingGrade[]>(
    sortingGradeUrl,
    "POST",
    sortingGradeLastYearRequest
  );

  const sortingGradeYearlyDiff = useMemo(() => {
    let combustionCurrent = 0;
    let recyclingCurrent = 0;
    let combustionLast = 0;
    let recyclingLast = 0;
    for (const entry of sortingGradeCurrentYear ?? []) {
      if (entry.artikkel === 205) {
        combustionCurrent += entry.mengde;
      } else {
        recyclingCurrent += entry.mengde;
      }
    }
    for (const entry of sortingGradeLastYear ?? []) {
      if (entry.artikkel === 205) {
        combustionLast += entry.mengde;
      } else {
        recyclingLast += entry.mengde;
      }
    }

    const totalCurrent = combustionCurrent + recyclingCurrent;
    const totalLast = combustionLast + recyclingLast;
    const percentageCurrent = (recyclingCurrent / totalCurrent) * 100;
    const percentageLast = (recyclingLast / totalLast) * 100;
    return Math.round((percentageCurrent - percentageLast) * 10) / 10;
  }, [sortingGradeCurrentYear, sortingGradeLastYear]);

  const isIncreaseFromLastYear = useMemo(
    () => sortingGradeYearlyDiff > 0,
    [sortingGradeYearlyDiff]
  );

  const handleCopyAddress = useCallback(() => {
    setAddressCopied(true);
    navigator.clipboard.writeText(
      (customerData?.[0]?.adresse ?? "") +
        ", " +
        (customerData?.[0]?.postnummer ?? "") +
        " " +
        (customerData?.[0]?.by ?? "")
    );
    setTimeout(() => {
      setAddressCopied(false);
    }, 2000);
  }, [customerData]);

  const hasAddress = useMemo(() => {
    return customerData?.[0]?.adresse && customerData?.[0]?.adresse.length > 0;
  }, [customerData]);

  const translateDay = useCallback((day: string) => {
    switch (day) {
      case "Måndag":
        return t("common.monday");
      case "Tysdag":
        return t("common.tuesday");
      case "Onsdag":
        return t("common.wednesday");
      case "Torsdag":
        return t("common.thursday");
      case "Fredag":
        return t("common.friday");
      case "Laurdag":
        return t("common.saturday");
      case "Søndag":
        return t("common.sunday");
      default:
        return "";
    }
  }, []);

  return (
    <PageLayout
      title={t("pages.customers.title") as string}
      pdfTitle={
        isOrderlineView
          ? `ngir_${customerData?.[0].navn}_orderlines`
          : `ngir_${customerData?.[0].navn}_diagrams`
      }
      handleGoBack={() => setIsOrderlineView(false)}
      isOrderViewPage={isOrderlineView}
    >
      <div className="customer-info">
        <div className="customer-info__top">
          <div className="customer-info__top__meta-data">
            <Text
              className="customer-info__top__meta-data__title"
              text={customerData?.[0].navn ?? ""}
              size="small"
              color="dark"
              fontWeight={600}
            />
            <div className="customer-info__top__meta-data__address">
              <Text
                text={
                  (customerData?.[0]?.adresse ?? "") +
                  (hasAddress ? ", " : "") +
                  (customerData?.[0]?.postnummer ?? "") +
                  " " +
                  (customerData?.[0]?.by ?? "")
                }
                size="xxs"
                color="dark-contrast"
              />
              {hasAddress && (
                <>
                  {addressCopied ? (
                    <Icon name="checkmark" />
                  ) : (
                    <CopyClipboardIcon onClick={handleCopyAddress} />
                  )}
                </>
              )}
            </div>
            <Text
              text={t("pages.customers.customerId") + ": " + customerId}
              size="xxs"
              color="dark-contrast"
            />
          </div>
          <div className="customer-info__top__pickup-day">
            <Text text={t("pages.customersInfo.pickupThisWeek")} size="xxxs" />
            <div className="customer-info__top__pickup-day__garbage-type">
              <div
                className={`customer-info__top__pickup-day__garbage-type__item ${
                  !isWaste ? "transparent" : ""
                }`}
              >
                <div className="customer-info__top__pickup-day__garbage-type__item__container">
                  <WasteIcon />
                </div>
                <div className="customer-info__top__pickup-day__garbage-type__item__text">
                  <Text text="Rest" size="xxs" className="type" />
                  <div className="days">
                    {wastePickupDays.length > 0 ? (
                      wastePickupDays.map((element, index) => (
                        <div key={index}>
                          {index === 0 && <Text text="-" size="xxs" />}
                          <Text
                            text={translateDay(element)}
                            size="xxs"
                            fontWeight={600}
                          />
                          {index < wastePickupDays.length - 1 && (
                            <Text text="og" size="xxs" />
                          )}
                        </div>
                      ))
                    ) : (
                      <>
                        <Text text="-" size="xxs" />
                        <Text
                          text={t("pages.customersInfo.noPickup")}
                          size="xxs"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={`customer-info__top__pickup-day__garbage-type__item ${
                  !isPaper ? "transparent" : ""
                }`}
              >
                <div className="customer-info__top__pickup-day__garbage-type__item__container">
                  <PaperIcon />
                </div>
                <div className="customer-info__top__pickup-day__garbage-type__item__text">
                  <Text text="PPP" size="xxs" className="type" />
                  <div className="days">
                    {paperPickupDays.length > 0 ? (
                      paperPickupDays.map((element, index) => (
                        <div key={index}>
                          {index === 0 && <Text text="-" size="xxs" />}
                          <Text
                            text={translateDay(element)}
                            size="xxs"
                            fontWeight={600}
                          />
                          {index < paperPickupDays.length - 1 && (
                            <Text text="og" size="xxs" />
                          )}
                        </div>
                      ))
                    ) : (
                      <>
                        <Text text="-" size="xxs" />
                        <Text
                          text={t("pages.customersInfo.noPickup")}
                          size="xxs"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div
                className={`customer-info__top__pickup-day__garbage-type__item ${
                  !isBio ? "transparent" : ""
                }`}
              >
                <div className="customer-info__top__pickup-day__garbage-type__item__container">
                  <BioIcon />
                </div>
                <div className="customer-info__top__pickup-day__garbage-type__item__text">
                  <Text text="Bio" size="xxs" className="type" />
                  <div className="days">
                    {bioPickupDays.length > 0 ? (
                      bioPickupDays.map((element, index) => (
                        <div key={index}>
                          {index === 0 && <Text text="-" size="xxs" />}
                          <Text
                            text={translateDay(element)}
                            size="xxs"
                            fontWeight={600}
                          />
                          {index < bioPickupDays.length - 1 && (
                            <Text text="og" size="xxs" />
                          )}
                        </div>
                      ))
                    ) : (
                      <>
                        <Text text="-" size="xxs" />
                        <Text
                          text={t("pages.customersInfo.noPickup")}
                          size="xxs"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="customer-info__top__sorting-grade">
            <Text
              text={t("pages.customersInfo.sortingGradeLastMonth")}
              size="xxxs"
            />
            <div className="customer-info__top__sorting-grade__container">
              <SortingGradeIcon />

              <Text
                className="big-percentage"
                text={sortingGradeLastMonthData + "%"}
                size="big"
                fontWeight={600}
              />
            </div>
            <div className="customer-info__top__sorting-grade__container__percentages__diff">
              <Icon name="arrow" rotate={isIncreaseFromLastYear ? 270 : 90} />
              <Text
                text={`${sortingGradeYearlyDiff}% fra gjennomsnittet i ${
                  currentYear - 1
                }`}
                size="xxxs"
              />
            </div>
          </div>
        </div>

        {!isOrderlineView && (
          <>
            <div className="customer-info__diagram-container">
              <div className="customer-info__diagram-container__waste-amount">
                <WasteAmount
                  dataYear1={dataYear1}
                  dataYear2={dataYear2}
                  handleFirstSelect={handleFirstSelect}
                  handleSecondSelect={handleSecondSelect}
                  firstSelectedYear={year1}
                  secondSelectedYear={year2}
                  compareViewActive={compareViewActive}
                  setCompareViewActive={setCompareViewActive}
                  isMonth={isMonth}
                  setIsMonth={setIsMonth}
                  selectedMonth={selectedMonth}
                  setSelectedMonth={setSelectedMonth}
                />
              </div>
              <div className="customer-info__diagram-container__sorting-grade">
                <SortingGrade
                  year1={year1}
                  year2={year2}
                  customerId={customerId}
                  dataYear1={dataYear1}
                  dataYear2={dataYear2}
                  selectedMonth={selectedMonth}
                  compareViewActive={compareViewActive}
                  isMonth={isMonth}
                />
              </div>
            </div>

            <div
              className="customer-info__subscription-container"
              id="subscriptionTable"
            >
              <SubscriptionTable
                customerId={customerId}
                customerName={customerData?.[0]?.navn}
              />
            </div>
            <div className="customer-info__order-container" id="orderTable">
              <OrderTable
                customerId={customerId}
                setOrderId={handleSetOrderId}
                setIsOrderlineView={handleSetIsOrderlineViewTrue}
              />
            </div>
          </>
        )}
        {isOrderlineView && (
          <div>
            <OrderlineView
              orderId={orderId}
              customerId={customerId}
              setIsOrderlineView={handleSetIsOrderlineViewFalse}
            />
          </div>
        )}
      </div>
    </PageLayout>
  );
};
